// OperationsCampDetails.jsx
//
// Camp Details page for the Operations Dashboard.
//
// Route:
// /dashboard/operations/camp/:campId
//
// Tabs:
// - Overview
// - Cases
// - Technicians
//
// Overview:
// - Camp information
// - Financial target (contract value, cost target, utilization) [NEW]
// - Overall camp progress
// - Patient/service metrics
// - Service-wise progress
//
// Cases / Technicians:
// - Lazy loaded
// - Cached after first load
//
// Designed to work with the shared operationsUi.jsx design system.

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';

import {
  useParams,
  useNavigate,
} from 'react-router-dom';

import {
  getOperationsCampDetail,
  getOperationsCampCases,
  getOperationsCampTechnicians,
  getOperationsCampServices,
  getOperationsCampFinancialTarget,
  updateOperationsCampFinancialTarget,
} from './operationsApi';

import {
  formatDisplayDate,
  getOperationsErrorMessage,
  formatINR,
  num,
} from './operationHelpers';

import {
  BRAND,
  RadialProgress,
  ProgressBar,
  StatusBadge,
  SkeletonBlock,
  Card,
  SectionHeader,
  MetricCard,
  Avatar,
  EmptyState,
  ErrorState,

  IconArrowLeft,
  IconPulse,
  IconMapPin,
  IconCalendar,
  IconBuilding,
  IconUsers,
  IconStethoscope,
  IconClipboard,
  IconCheckCircle,
  IconClock,
  IconAlertTriangle,
} from './operationsUi';


/* ============================================================================
 * Constants
 * ========================================================================== */

const TABS = [
  {
    key: 'overview',
    label: 'Overview',
    icon: IconClipboard,
  },
  {
    key: 'cases',
    label: 'Cases',
    icon: IconUsers,
  },
  {
    key: 'technicians',
    label: 'Technicians',
    icon: IconStethoscope,
  },
];


/* ============================================================================
 * Utility helpers
 * ========================================================================== */

function clamp(value) {
  const n = Number(value);

  if (!Number.isFinite(n)) {
    return 0;
  }

  return Math.max(0, Math.min(100, n));
}


function getServiceProgress(service) {
  if (!service) {
    return 0;
  }

  if (service.progress_percentage !== undefined) {
    return clamp(service.progress_percentage);
  }

  if (service.progress !== undefined) {
    return clamp(service.progress);
  }

  const total = Number(
    service.total_cases ??
    service.total ??
    0
  );

  const completed = Number(
    service.completed_cases ??
    service.completed ??
    0
  );

  if (total <= 0) {
    return 0;
  }

  return clamp(
    Math.round((completed / total) * 100)
  );
}


function getServiceStatus(service) {
  const progress = getServiceProgress(service);

  const explicitStatus =
    service?.status ||
    service?.service_status;

  if (explicitStatus) {
    return explicitStatus;
  }

  if (progress >= 100) {
    return 'COMPLETED';
  }

  if (progress > 0) {
    return 'IN_PROGRESS';
  }

  return 'PENDING';
}


function getServiceName(service) {
  return (
    service?.service_name ||
    service?.name ||
    service?.service?.name ||
    service?.service?.service_name ||
    'Unnamed Service'
  );
}


function getServiceId(service, index) {
  return (
    service?.service_id ??
    service?.id ??
    service?.service?.id ??
    `service-${index}`
  );
}


/* ============================================================================
 * Info Row
 * ========================================================================== */

function InfoRow({
  icon: Icon,
  label,
  value,
}) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-slate-100 py-3 last:border-b-0">
      <span className="flex min-w-0 items-center gap-2 text-sm text-slate-500">
        {Icon && (
          <Icon className="h-4 w-4 shrink-0 text-slate-400" />
        )}

        <span>{label}</span>
      </span>

      <span className="max-w-[60%] truncate text-right text-sm font-semibold text-slate-900">
        {value ?? '—'}
      </span>
    </div>
  );
}


/* ============================================================================
 * Financial Target Section [NEW]
 * ========================================================================== */

/**
 * Resolve the "Cost Target" label shown next to Contract Value.
 * PERCENTAGE -> "30%"
 * AMOUNT     -> "₹15,000"
 * Anything unset -> "Not configured"
 */
function getCostTargetDisplay(ft) {
  if (!ft || !ft.cost_target_type) {
    return 'Not configured';
  }

  if (ft.cost_target_type === 'PERCENTAGE') {
    if (ft.cost_target_percentage === null || ft.cost_target_percentage === undefined) {
      return 'Not configured';
    }

    return `${Number(ft.cost_target_percentage)}%`;
  }

  if (ft.cost_target_type === 'AMOUNT') {
    return formatINR(ft.cost_target_amount, { fallback: 'Not configured' });
  }

  return 'Not configured';
}


function getTargetStatusLabel(status) {
  switch (status) {
    case 'WITHIN_TARGET':
      return 'Within Target';
    case 'EXCEEDED':
      return 'Target Exceeded';
    case 'NOT_CONFIGURED':
      return 'Target Not Configured';
    default:
      return status || 'Unknown';
  }
}


/**
 * Inline edit form for Contract Value.
 * This is the ONLY field Operations is allowed to change.
 */
function EditContractValueForm({
  initialValue,
  saving,
  saveError,
  onSave,
  onCancel,
}) {
  const [value, setValue] = useState(
    initialValue !== null && initialValue !== undefined
      ? String(initialValue)
      : ''
  );

  const [validationError, setValidationError] = useState(null);

  const handleSave = () => {
    const trimmed = value.trim();

    if (!trimmed) {
      setValidationError('Contract value is required.');
      return;
    }

    // Reject anything that isn't a plain non-negative number
    // (no letters, no negative sign, no extra symbols).
    if (!/^\d+(\.\d+)?$/.test(trimmed)) {
      setValidationError('Enter a valid numeric amount.');
      return;
    }

    const numeric = Number(trimmed);

    if (!Number.isFinite(numeric) || numeric < 0) {
      setValidationError('Contract value cannot be negative.');
      return;
    }

    setValidationError(null);
    onSave(numeric);
  };

  return (
    <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
      <input
        type="number"
        min="0"
        step="0.01"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={saving}
        className="w-full max-w-[200px] rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#11a8a4]"
        placeholder="Enter contract value"
      />

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded-lg bg-[#11a8a4] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#0b7d7a] disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>

        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>

      {(validationError || saveError) && (
        <span className="text-xs font-medium text-red-600">
          {validationError || getOperationsErrorMessage(saveError)}
        </span>
      )}
    </div>
  );
}


function FinancialTargetSection({
  campId,
  financialTarget,
  loading,
  error,
  onUpdated,
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  if (loading) {
    return <SkeletonBlock className="h-64" />;
  }

  if (error) {
    return (
      <Card className="p-5">
        <SectionHeader
          title="Financial Target"
          description="Cost target vs. actual spend for this camp."
          icon={IconClipboard}
        />

        <div className="mt-4">
          <ErrorState
            title="Unable to load financial target"
            message="Unable to load financial target."
          />
        </div>
      </Card>
    );
  }

  const ft = financialTarget || {};

  const contractValue = ft.contract_value ?? null;
  const calculatedTarget = ft.calculated_target_amount ?? null;
  const actualCost = ft.actual_cost ?? null;
  const remaining = ft.remaining_target ?? null;
  const utilizationRaw = ft.target_utilization_percentage;
  const utilization = clamp(utilizationRaw);
  const status = ft.target_status || 'NOT_CONFIGURED';

  // Values for the "₹X / ₹Y" progress bar label — fall back to
  // calculated_target_amount if target_cost_amount isn't present.
  const targetCostForBar = Number(ft.target_cost_amount ?? calculatedTarget ?? 0);
  const actualCostForBar = Number(actualCost ?? 0);

  const handleSave = (numericValue) => {
    setSaving(true);
    setSaveError(null);

    // Backend remains the source of truth for all financial calculations —
    // we only send contract_value and let the server recompute everything
    // else, then refresh.
    updateOperationsCampFinancialTarget(campId, {
      contract_value: numericValue,
    })
      .then(() => {
        setSaving(false);
        setEditing(false);
        onUpdated?.();
      })
      .catch((err) => {
        setSaving(false);
        setSaveError(err);
      });
  };

  return (
    <Card className="p-5">

      <div className="flex items-start justify-between gap-3">
        <SectionHeader
          title="Financial Target"
          description="Cost target vs. actual spend for this camp."
          icon={IconClipboard}
        />

        <StatusBadge status={status} size="sm" dot />
      </div>

      {status === 'NOT_CONFIGURED' && (
        <div className="mt-4 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          Financial target has not been configured by Admin yet.
        </div>
      )}

      {/* Contract Value (editable) + Cost Target (read-only, set by Admin) */}
      <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            Contract Value
          </p>

          {!editing ? (
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <span className="text-lg font-bold text-slate-900">
                {formatINR(contractValue)}
              </span>

              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-xs font-semibold text-[#0b7d7a] transition-colors hover:text-[#0a6360]"
              >
                Edit Contract Value
              </button>
            </div>
          ) : (
            <EditContractValueForm
              initialValue={contractValue}
              saving={saving}
              saveError={saveError}
              onSave={handleSave}
              onCancel={() => {
                setEditing(false);
                setSaveError(null);
              }}
            />
          )}
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            Cost Target
          </p>

          <p className="mt-1 text-lg font-bold text-slate-900">
            {getCostTargetDisplay(ft)}
          </p>

          <p className="mt-0.5 text-[11px] text-slate-400">
            Set by Admin · Read-only
          </p>
        </div>

      </div>

      {/* Target Cost / Actual Cost / Remaining / Utilization */}
      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">

        <MetricCard
          label="Target Cost"
          value={formatINR(calculatedTarget, { fallback: 'Not configured' })}
          icon={IconClipboard}
          accent={BRAND.teal}
          className="shadow-none"
        />

        <MetricCard
          label="Actual Cost"
          value={formatINR(actualCost)}
          icon={IconClock}
          accent={BRAND.amber}
          className="shadow-none"
        />

        <MetricCard
          label="Remaining"
          value={formatINR(remaining)}
          icon={IconCheckCircle}
          accent={BRAND.green}
          className="shadow-none"
        />

        <MetricCard
          label="Utilization"
          value={
            utilizationRaw !== null && utilizationRaw !== undefined
              ? `${Number(utilizationRaw).toFixed(2)}%`
              : 'Not configured'
          }
          icon={IconPulse}
          accent="#6366f1"
          className="shadow-none"
        />

      </div>

      {/* Utilization progress bar — clamped 0-100, never visually exceeds 100% */}
      <div className="mt-5 rounded-2xl border border-slate-100 bg-slate-50 p-4">

        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Cost Utilization
          </span>

          <span className="text-sm font-bold tabular-nums text-slate-700">
            {formatINR(actualCostForBar)} / {formatINR(targetCostForBar)}
          </span>
        </div>

        <ProgressBar
          percentage={utilization}
          color={status === 'EXCEEDED' ? BRAND.red : BRAND.teal}
          trackColor="#dcefed"
          height="h-2.5"
        />

        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs font-semibold tabular-nums text-slate-500">
            {utilizationRaw !== null && utilizationRaw !== undefined
              ? `${Number(utilizationRaw).toFixed(2)}%`
              : '—'}
          </span>

          <span className="text-xs font-semibold text-slate-500">
            {getTargetStatusLabel(status)}
          </span>
        </div>

      </div>

      {/* Edit button is also reachable here for smaller screens when the
          top row wraps; keeping the primary one above near Contract Value
          per the spec's expected layout. */}
    </Card>
  );
}


/* ============================================================================
 * Service Progress Card
 * ========================================================================== */

function ServiceProgressCard({
  service,
}) {
  const progress = getServiceProgress(service);

  const total = num(
    service?.total_cases ??
    service?.total ??
    0
  );

  const completed = num(
    service?.completed_cases ??
    service?.completed ??
    0
  );

  const pending = num(
    service?.pending_cases ??
    service?.pending ??
    Math.max(0, total - completed)
  );

  const name = getServiceName(service);
  const status = getServiceStatus(service);

  const isCompleted = progress >= 100;

  const progressColor = isCompleted
    ? BRAND.green
    : progress > 0
      ? BRAND.amber
      : BRAND.teal;

  return (
    <div className="group rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-[#b7e8e5] hover:shadow-md">
      
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
            style={{
              backgroundColor: `${progressColor}15`,
              color: progressColor,
            }}
          >
            <IconClipboard className="h-5 w-5" />
          </div>

          <div className="min-w-0">
            <h4 className="truncate text-sm font-bold text-slate-900">
              {name}
            </h4>

            <p className="mt-0.5 text-xs text-slate-500">
              Service progress
            </p>
          </div>
        </div>

        <StatusBadge
          status={status}
          size="sm"
          dot
        />
      </div>


      {/* Progress */}
      <div className="mt-5 flex items-center gap-4">
        <RadialProgress
          percentage={progress}
          size={72}
          stroke={7}
          color={progressColor}
          trackColor="#f1f5f9"
          labelClassName="text-sm font-bold"
        />

        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-slate-500">
              Completion
            </span>

            <span
              className="text-sm font-bold tabular-nums"
              style={{ color: progressColor }}
            >
              {Math.round(progress)}%
            </span>
          </div>

          <ProgressBar
            percentage={progress}
            color={progressColor}
            height="h-2"
          />
        </div>
      </div>


      {/* Metrics */}
      <div className="mt-5 grid grid-cols-3 gap-2 border-t border-slate-100 pt-4">
        
        <div className="rounded-xl bg-slate-50 px-2.5 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Total
          </p>

          <p className="mt-0.5 text-base font-bold tabular-nums text-slate-900">
            {total}
          </p>
        </div>


        <div className="rounded-xl bg-green-50 px-2.5 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-green-600">
            Done
          </p>

          <p className="mt-0.5 text-base font-bold tabular-nums text-green-700">
            {completed}
          </p>
        </div>


        <div className="rounded-xl bg-amber-50 px-2.5 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-600">
            Pending
          </p>

          <p className="mt-0.5 text-base font-bold tabular-nums text-amber-700">
            {pending}
          </p>
        </div>

      </div>
    </div>
  );
}


/* ============================================================================
 * Service Progress Section
 * ========================================================================== */

function ServiceProgressSection({
  services,
}) {
  if (!services || services.length === 0) {
    return (
      <Card className="p-5">
        <SectionHeader
          title="Service-wise Progress"
          description="Progress breakdown for each service in this camp."
          icon={IconClipboard}
        />

        <div className="mt-5">
          <EmptyState
            title="No service progress available"
            description="Service-level progress will appear here once services are available for this camp."
            icon={IconClipboard}
          />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-5">
      <SectionHeader
        title="Service-wise Progress"
        description={`${services.length} service${services.length !== 1 ? 's' : ''} configured for this camp.`}
        icon={IconClipboard}
      />

      <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {services.map((service, index) => (
          <ServiceProgressCard
            key={getServiceId(service, index)}
            service={service}
          />
        ))}
      </div>
    </Card>
  );
}


/* ============================================================================
 * Overview Tab
 * ========================================================================== */

function OverviewTab({
  loading,
  error,
  servicesData,
  campData,
  campId,
  financialTarget,
  financialTargetLoading,
  financialTargetError,
  onFinancialTargetUpdated,
}) {
  if (loading) {
    return (
      <div className="space-y-5">
        
        {/* Top skeleton */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <SkeletonBlock className="h-[420px]" />
          <SkeletonBlock className="h-[420px]" />
        </div>

        {/* Financial target skeleton */}
        <SkeletonBlock className="h-64" />

        {/* Service skeleton */}
        <SkeletonBlock className="h-[360px]" />
      </div>
    );
  }


  const message = error
    ? getOperationsErrorMessage(error)
    : null;

  if (message) {
    return (
      <ErrorState
        title="Unable to load camp overview"
        message={message}
      />
    );
  }


  if (!campData) {
    return (
      <EmptyState
        title="Camp information unavailable"
        description="No information was returned for this camp."
      />
    );
  }


  const camp = campData?.camp || {};
  const summary = campData?.summary || {};


  /*
   * Support multiple possible backend structures.
   *
   * Preferred:
   * summary.services
   *
   * Also supports:
   * summary.service_progress
   * campData.services
   */
  const services =
    servicesData?.services ||
    servicesData?.service_progress ||
    servicesData?.data ||
    (Array.isArray(servicesData)
        ? servicesData
        : []) ||
    summary?.services ||
    summary?.service_progress ||
    campData?.services ||
    [];


  const overallProgress = clamp(
    summary?.progress_percentage
  );


  return (
    <div className="space-y-5">

      {/* ====================================================================
       * Top Section
       * ================================================================== */}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[0.95fr_1.05fr]">

        {/* Camp Information */}
        <Card className="overflow-hidden">
          
          <div className="border-b border-slate-100 px-5 py-4">
            <div className="flex items-center justify-between gap-3">
              
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  Camp Information
                </h3>

                <p className="mt-0.5 text-xs text-slate-500">
                  Basic camp configuration and location details
                </p>
              </div>

              <StatusBadge
                status={
                  campData?.status ||
                  (camp?.is_completed
                    ? 'COMPLETED'
                    : 'SCHEDULED')
                }
              />
            </div>
          </div>


          <div className="px-5 py-2">

            <InfoRow
              icon={IconBuilding}
              label="Client"
              value={camp?.client?.name}
            />

            <InfoRow
              icon={IconMapPin}
              label="Location"
              value={camp?.location}
            />

            <InfoRow
              icon={IconBuilding}
              label="Branch Name"
              value={camp?.branch_name}
            />

            <InfoRow
              label="Branch Code"
              value={camp?.branch_code}
            />

            <InfoRow
              icon={IconMapPin}
              label="District"
              value={camp?.district}
            />

            <InfoRow
              icon={IconMapPin}
              label="State"
              value={camp?.state}
            />

            <InfoRow
              label="Pin Code"
              value={camp?.pin_code}
            />

            <InfoRow
              icon={IconCalendar}
              label="Start Date"
              value={
                camp?.start_date
                  ? formatDisplayDate(camp.start_date)
                  : null
              }
            />

            <InfoRow
              icon={IconCalendar}
              label="End Date"
              value={
                camp?.end_date
                  ? formatDisplayDate(camp.end_date)
                  : null
              }
            />

            <InfoRow
              label="Ready to Go"
              value={
                camp?.ready_to_go === true
                  ? 'Yes'
                  : camp?.ready_to_go === false
                    ? 'No'
                    : null
              }
            />

            <InfoRow
              label="Billing Mode"
              value={camp?.billing_mode}
            />

          </div>
        </Card>


        {/* Progress Summary */}
        <Card className="overflow-hidden">

          <div className="border-b border-slate-100 px-5 py-4">
            <SectionHeader
              title="Progress Summary"
              description="Overall execution progress for this camp."
              icon={IconPulse}
            />
          </div>


          <div className="p-5">

            {/* Main progress area */}
            <div className="flex flex-col items-center gap-6 sm:flex-row">

              <div className="flex shrink-0 flex-col items-center">
                
                <RadialProgress
                  percentage={overallProgress}
                  size={150}
                  stroke={12}
                  color={BRAND.teal}
                  trackColor={BRAND.tealLight}
                  labelClassName="text-2xl font-bold"
                />

                <p className="mt-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
                  Overall Progress
                </p>

              </div>


              <div className="grid w-full grid-cols-2 gap-3">

                <MetricCard
                  label="Total Patients"
                  value={num(summary?.total_patients)}
                  icon={IconUsers}
                  accent={BRAND.teal}
                  className="shadow-none"
                />

                <MetricCard
                  label="Total Services"
                  value={num(summary?.total_services)}
                  icon={IconClipboard}
                  accent="#6366f1"
                  className="shadow-none"
                />

                <MetricCard
                  label="Completed"
                  value={num(summary?.completed_services)}
                  icon={IconCheckCircle}
                  accent={BRAND.green}
                  className="shadow-none"
                />

                <MetricCard
                  label="Pending"
                  value={num(summary?.pending_services)}
                  icon={IconClock}
                  accent={BRAND.amber}
                  className="shadow-none"
                />

              </div>

            </div>


            {/* Overall progress bar */}
            <div className="mt-6 rounded-2xl border border-slate-100 bg-slate-50 p-4">

              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Camp Completion
                </span>

                <span className="text-sm font-bold tabular-nums text-[#0b7d7a]">
                  {Math.round(overallProgress)}%
                </span>
              </div>

              <ProgressBar
                percentage={overallProgress}
                color={BRAND.teal}
                trackColor="#dcefed"
                height="h-2.5"
              />

            </div>


            {/* Summary status */}
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">

              <div className="flex items-center gap-3 rounded-xl border border-green-100 bg-green-50 p-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-green-100 text-green-600">
                  <IconCheckCircle className="h-4 w-4" />
                </div>

                <div>
                  <p className="text-xs text-green-600">
                    Completed Services
                  </p>

                  <p className="text-lg font-bold text-green-700">
                    {num(summary?.completed_services)}
                  </p>
                </div>
              </div>


              <div className="flex items-center gap-3 rounded-xl border border-amber-100 bg-amber-50 p-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
                  <IconClock className="h-4 w-4" />
                </div>

                <div>
                  <p className="text-xs text-amber-600">
                    Pending Services
                  </p>

                  <p className="text-lg font-bold text-amber-700">
                    {num(summary?.pending_services)}
                  </p>
                </div>
              </div>


              <div className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                  <IconUsers className="h-4 w-4" />
                </div>

                <div>
                  <p className="text-xs text-slate-500">
                    Patients
                  </p>

                  <p className="text-lg font-bold text-slate-800">
                    {num(summary?.total_patients)}
                  </p>
                </div>
              </div>

            </div>

          </div>
        </Card>

      </div>


      {/* ====================================================================
       * Financial Target [NEW]
       * ================================================================== */}

      <FinancialTargetSection
        campId={campId}
        financialTarget={financialTarget}
        loading={financialTargetLoading}
        error={financialTargetError}
        onUpdated={onFinancialTargetUpdated}
      />


      {/* ====================================================================
       * Service-wise Progress
       * ================================================================== */}

      <ServiceProgressSection
        services={services}
      />

    </div>
  );
}


/* ============================================================================
 * Cases Tab
 * ========================================================================== */

function CasesTab({
  loading,
  error,
  casesData,
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, index) => (
          <SkeletonBlock
            key={index}
            className="h-12"
          />
        ))}
      </div>
    );
  }


  const message = error
    ? getOperationsErrorMessage(error)
    : null;

  if (message) {
    return (
      <ErrorState
        title="Unable to load cases"
        message={message}
      />
    );
  }


  const cases = casesData?.cases || [];


  if (cases.length === 0) {
    return (
      <EmptyState
        title="No cases found"
        description="There are no patient cases available for this camp."
        icon={IconUsers}
      />
    );
  }


  return (
    <Card className="overflow-hidden">

      <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-slate-800">
            Patient Cases
          </p>

          <p className="text-xs text-slate-500">
            Patient-level service execution
          </p>
        </div>

        <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200">
          Total: {num(
            casesData?.total_cases,
            cases.length
          )}
        </span>
      </div>


      <div className="max-h-[36rem] overflow-x-auto overflow-y-auto">
        <table className="min-w-full text-sm">

          <thead className="sticky top-0 z-10 bg-[#e6f7f6] text-xs uppercase text-[#0b7570]">
            <tr>
              <th className="px-4 py-3 text-left">
                Patient ID
              </th>

              <th className="px-4 py-3 text-left">
                Patient Name
              </th>

              <th className="px-4 py-3 text-left">
                Checked In
              </th>

              <th className="px-4 py-3 text-left">
                Test Date
              </th>

              <th className="px-4 py-3 text-left">
                Services
              </th>

              <th className="px-4 py-3 text-left">
                Technician
              </th>

              <th className="px-4 py-3 text-left">
                Status
              </th>
            </tr>
          </thead>


          <tbody>
            {cases.map((item, rowIndex) => {

              const services =
                item?.services || [];

              return (
                <tr
                  key={
                    item?.patient_id ??
                    item?.unique_patient_id ??
                    rowIndex
                  }
                  className={`border-t border-slate-100 align-top ${
                    rowIndex % 2 === 1
                      ? 'bg-slate-50/60'
                      : 'bg-white'
                  } hover:bg-[#f7fbfb]`}
                >

                  <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                    {item?.unique_patient_id ??
                      item?.patient_id ??
                      '—'}
                  </td>


                  <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-900">
                    {item?.patient_name || '—'}
                  </td>


                  <td className="px-4 py-3">
                    {item?.checked_in ? (
                      <span className="font-semibold text-green-700">
                        Yes
                      </span>
                    ) : (
                      <span className="text-slate-400">
                        No
                      </span>
                    )}
                  </td>


                  <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                    {item?.test_date
                      ? formatDisplayDate(
                          item.test_date
                        )
                      : '—'}
                  </td>


                  <td className="px-4 py-3">
                    {services.length === 0 ? (
                      <span className="text-slate-400">
                        No services
                      </span>
                    ) : (
                      <div className="space-y-1.5">
                        {services.map(
                          (service, index) => (
                            <div
                              key={
                                service?.service_id ??
                                index
                              }
                              className="font-medium text-slate-700"
                            >
                              {service?.service_name ||
                                'Unnamed Service'}
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </td>


                  <td className="px-4 py-3">
                    {services.length === 0 ? (
                      <span className="text-slate-400">
                        —
                      </span>
                    ) : (
                      <div className="space-y-1.5">
                        {services.map(
                          (service, index) => (
                            <div
                              key={
                                service?.service_id ??
                                index
                              }
                              className="text-slate-700"
                            >
                              {service?.technician_name ||
                                '—'}
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </td>


                  <td className="px-4 py-3">
                    {services.length === 0 ? (
                      '—'
                    ) : (
                      <div className="space-y-1.5">
                        {services.map(
                          (service, index) => (
                            <div
                              key={
                                service?.service_id ??
                                index
                              }
                            >
                              <StatusBadge
                                status={
                                  service?.status ||
                                  (service?.is_completed
                                    ? 'COMPLETED'
                                    : 'PENDING')
                                }
                                dot
                              />
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </td>

                </tr>
              );
            })}
          </tbody>

        </table>
      </div>
    </Card>
  );
}


/* ============================================================================
 * Technician helpers
 * ========================================================================== */

function getInitials(name) {
  if (!name) {
    return '?';
  }

  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(
      (part) =>
        part.charAt(0).toUpperCase()
    )
    .join('');
}


/* ============================================================================
 * Technician Card
 * ========================================================================== */

function TechnicianCard({
  tech,
}) {
  const services =
    tech?.services || [];

  const total = num(
    tech?.total_cases
  );

  const completed = num(
    tech?.completed_cases
  );

  const pending = num(
    tech?.pending_cases
  );

  const progress =
    total > 0
      ? clamp(
          (completed / total) * 100
        )
      : 0;


  return (
    <Card
      hover
      className="p-4"
    >

      {/* Header */}
      <div className="flex items-start justify-between gap-3">

        <div className="flex min-w-0 items-center gap-3">

          <Avatar
            name={tech?.technician_name}
            size="md"
          />

          <div className="min-w-0">
            <p className="truncate font-semibold text-slate-900">
              {tech?.technician_name ||
                'Unnamed Technician'}
            </p>

            <p className="mt-0.5 text-xs text-slate-500">
              {tech?.resource_type || 'Technician'}
              {' · '}
              {tech?.status || '—'}
            </p>
          </div>

        </div>


        <RadialProgress
          percentage={progress}
          size={48}
          stroke={5}
          showLabel={false}
        />

      </div>


      {/* Technician metrics */}
      <div className="mt-4 grid grid-cols-3 gap-2">

        <div className="rounded-xl bg-slate-50 p-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Total
          </p>

          <p className="mt-1 font-bold tabular-nums text-slate-900">
            {total}
          </p>
        </div>


        <div className="rounded-xl bg-green-50 p-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-green-600">
            Done
          </p>

          <p className="mt-1 font-bold tabular-nums text-green-700">
            {completed}
          </p>
        </div>


        <div className="rounded-xl bg-amber-50 p-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-600">
            Pending
          </p>

          <p className="mt-1 font-bold tabular-nums text-amber-700">
            {pending}
          </p>
        </div>

      </div>


      {/* Technician services */}
      {services.length === 0 ? (
        <div className="mt-4 border-t border-slate-100 pt-4">
          <p className="text-xs text-slate-400">
            No services assigned.
          </p>
        </div>
      ) : (
        <div className="mt-4 space-y-3 border-t border-slate-100 pt-4">

          {services.map(
            (service, index) => {

              const progress =
                getServiceProgress(
                  service
                );

              return (
                <div
                  key={
                    service?.service_id ??
                    index
                  }
                >

                  <div className="mb-1.5 flex items-center justify-between gap-2">

                    <span className="truncate text-xs font-semibold text-slate-700">
                      {getServiceName(
                        service
                      )}
                    </span>

                    <span className="shrink-0 text-xs font-semibold tabular-nums text-slate-500">
                      {Math.round(progress)}%
                    </span>

                  </div>


                  <ProgressBar
                    percentage={progress}
                    height="h-1.5"
                    color={
                      progress >= 100
                        ? BRAND.green
                        : BRAND.teal
                    }
                  />


                  <div className="mt-1.5 flex justify-between text-[10px] text-slate-400">

                    <span>
                      Total:{' '}
                      {num(
                        service?.total_cases
                      )}
                    </span>

                    <span>
                      Done:{' '}
                      {num(
                        service?.completed_cases
                      )}
                    </span>

                    <span>
                      Pending:{' '}
                      {num(
                        service?.pending_cases
                      )}
                    </span>

                  </div>

                </div>
              );
            }
          )}

        </div>
      )}

    </Card>
  );
}


/* ============================================================================
 * Technicians Tab
 * ========================================================================== */

function TechniciansTab({
  loading,
  error,
  techData,
}) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {Array.from({ length: 4 }).map(
          (_, index) => (
            <SkeletonBlock
              key={index}
              className="h-52"
            />
          )
        )}
      </div>
    );
  }


  const message = error
    ? getOperationsErrorMessage(error)
    : null;

  if (message) {
    return (
      <ErrorState
        title="Unable to load technicians"
        message={message}
      />
    );
  }


  const technicians =
    techData?.technicians || [];


  if (technicians.length === 0) {
    return (
      <EmptyState
        title="No technicians assigned"
        description="There are currently no technicians assigned to this camp."
        icon={IconStethoscope}
      />
    );
  }


  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {technicians.map(
        (technician, index) => (
          <TechnicianCard
            key={
              technician?.technician_id ??
              technician?.id ??
              index
            }
            tech={technician}
          />
        )
      )}
    </div>
  );
}


/* ============================================================================
 * Page
 * ========================================================================== */

export default function OperationsCampDetails() {

  const { campId } =
    useParams();

  const navigate =
    useNavigate();


  /* ------------------------------------------------------------------------
   * Tab state
   * ---------------------------------------------------------------------- */

  const [activeTab, setActiveTab] =
    useState('overview');


  /* ------------------------------------------------------------------------
   * API state
   * ---------------------------------------------------------------------- */

  const [overview, setOverview] =
    useState({
      data: null,
      loading: true,
      error: null,
    });


  const [cases, setCases] =
    useState({
      data: null,
      loading: false,
      error: null,
      loaded: false,
    });


  const [technicians, setTechnicians] =
    useState({
      data: null,
      loading: false,
      error: null,
      loaded: false,
    });

  const [services, setServices] =
    useState({
        data: null,
        loading: false,
        error: null,
        loaded: false,
    });

  /*
   * Financial target state.
   *
   * `source` tracks where the data came from so refreshes know which
   * endpoint to hit again:
   * - 'embedded'   -> overview.data.financial_target was present, so a
   *                   refresh re-fetches getOperationsCampDetail(campId).
   * - 'standalone' -> the main camp detail response had no financial_target
   *                   field, so a refresh re-fetches
   *                   getOperationsCampFinancialTarget(campId) directly.
   */
  const [financialTarget, setFinancialTarget] =
    useState({
      data: null,
      loading: false,
      error: null,
      source: null,
    });


  /* ------------------------------------------------------------------------
   * Load overview
   * ---------------------------------------------------------------------- */

  useEffect(() => {

    if (!campId) {
      return;
    }


    setOverview({
      data: null,
      loading: true,
      error: null,
    });


    setCases({
      data: null,
      loading: false,
      error: null,
      loaded: false,
    });


    setTechnicians({
      data: null,
      loading: false,
      error: null,
      loaded: false,
    });

    setServices({
      data: null,
      loading: false,
      error: null,
      loaded: false,
    });

    setFinancialTarget({
      data: null,
      loading: false,
      error: null,
      source: null,
    });


    setActiveTab('overview');


    let cancelled = false;


    getOperationsCampDetail(campId)
      .then((response) => {

        if (cancelled) {
          return;
        }

        setOverview({
          data: response,
          loading: false,
          error: null,
        });

      })
      .catch((error) => {

        if (cancelled) {
          return;
        }

        setOverview({
          data: null,
          loading: false,
          error,
        });

      });


    return () => {
      cancelled = true;
    };

  }, [campId]);


  /* ------------------------------------------------------------------------
   * Load financial target
   *
   * Preferred: use overview.data.financial_target if the backend already
   * includes it in the main camp detail response — avoids a second request.
   * Fallback: call getOperationsCampFinancialTarget(campId) separately.
   * ---------------------------------------------------------------------- */

  useEffect(() => {

    if (
      overview.loading ||
      overview.error ||
      !overview.data ||
      !campId
    ) {
      return;
    }

    if (overview.data.financial_target !== undefined) {
      setFinancialTarget({
        data: overview.data.financial_target,
        loading: false,
        error: null,
        source: 'embedded',
      });
      return;
    }

    let cancelled = false;

    setFinancialTarget({
      data: null,
      loading: true,
      error: null,
      source: 'standalone',
    });

    getOperationsCampFinancialTarget(campId)
      .then((response) => {

        if (cancelled) {
          return;
        }

        setFinancialTarget({
          data: response?.financial_target ?? null,
          loading: false,
          error: null,
          source: 'standalone',
        });

      })
      .catch((error) => {

        if (cancelled) {
          return;
        }

        setFinancialTarget({
          data: null,
          loading: false,
          error,
          source: 'standalone',
        });

      });

    return () => {
      cancelled = true;
    };

  }, [
    overview.loading,
    overview.error,
    overview.data,
    campId,
  ]);


  /*
   * Refresh financial target after Operations edits contract_value.
   * Re-fetches from whichever source originally provided the data, per
   * the "REFRESH BEHAVIOR" requirement — no manual browser refresh needed.
   */
  const refreshFinancialTarget = useCallback(() => {

    if (!campId) {
      return;
    }

    if (financialTarget.source === 'embedded') {

      getOperationsCampDetail(campId)
        .then((response) => {

          setOverview({
            data: response,
            loading: false,
            error: null,
          });

          setFinancialTarget({
            data: response?.financial_target ?? null,
            loading: false,
            error: null,
            source: 'embedded',
          });

        })
        .catch((error) => {

          setFinancialTarget((previous) => ({
            ...previous,
            loading: false,
            error,
          }));

        });

      return;
    }

    setFinancialTarget((previous) => ({
      ...previous,
      loading: true,
      error: null,
    }));

    getOperationsCampFinancialTarget(campId)
      .then((response) => {

        setFinancialTarget({
          data: response?.financial_target ?? null,
          loading: false,
          error: null,
          source: 'standalone',
        });

      })
      .catch((error) => {

        setFinancialTarget((previous) => ({
          ...previous,
          loading: false,
          error,
        }));

      });

  }, [campId, financialTarget.source]);


  /* ------------------------------------------------------------------------
   * Load cases lazily
   * ---------------------------------------------------------------------- */

  useEffect(() => {

    if (
      activeTab !== 'cases' ||
      cases.loaded ||
      !campId
    ) {
      return;
    }


    let cancelled = false;


    setCases((previous) => ({
      ...previous,
      loading: true,
      error: null,
    }));


    getOperationsCampCases(campId)
      .then((response) => {

        if (cancelled) {
          return;
        }

        setCases({
          data: response,
          loading: false,
          error: null,
          loaded: true,
        });

      })
      .catch((error) => {

        if (cancelled) {
          return;
        }

        setCases({
          data: null,
          loading: false,
          error,
          loaded: true,
        });

      });


    return () => {
      cancelled = true;
    };

  }, [
    activeTab,
    campId,
    cases.loaded,
  ]);


  /* ------------------------------------------------------------------------
   * Load technicians lazily
   * ---------------------------------------------------------------------- */

  useEffect(() => {

    if (
      activeTab !== 'technicians' ||
      technicians.loaded ||
      !campId
    ) {
      return;
    }


    let cancelled = false;


    setTechnicians((previous) => ({
      ...previous,
      loading: true,
      error: null,
    }));


    getOperationsCampTechnicians(campId)
      .then((response) => {

        if (cancelled) {
          return;
        }

        setTechnicians({
          data: response,
          loading: false,
          error: null,
          loaded: true,
        });

      })
      .catch((error) => {

        if (cancelled) {
          return;
        }

        setTechnicians({
          data: null,
          loading: false,
          error,
          loaded: true,
        });

      });


    return () => {
      cancelled = true;
    };

  }, [
    activeTab,
    campId,
    technicians.loaded,
  ]);

  /* ------------------------------------------------------------------------
    * Load services
    * ---------------------------------------------------------------------- */

    useEffect(() => {

    if (
        !campId ||
        services.loaded
    ) {
        return;
    }


    let cancelled = false;


    setServices((previous) => ({
        ...previous,
        loading: true,
        error: null,
    }));


    getOperationsCampServices(campId)
        .then((response) => {

        if (cancelled) {
            return;
        }

        console.log(
            'Operations Camp Services API:',
            response
        );


        setServices({
            data: response,
            loading: false,
            error: null,
            loaded: true,
        });

        })
        .catch((error) => {

        if (cancelled) {
            return;
        }

        console.error(
            'Operations Camp Services API Error:',
            error
        );


        setServices({
            data: null,
            loading: false,
            error,
            loaded: true,
        });

        });


    return () => {
        cancelled = true;
    };

    }, [
    campId,
    services.loaded,
    ]);


  /* ------------------------------------------------------------------------
   * Navigation
   * ---------------------------------------------------------------------- */

  const handleBack =
    useCallback(() => {

      if (
        window.history.length > 1
      ) {
        navigate(-1);
      } else {
        navigate(
          '/dashboard/operations'
        );
      }

    }, [navigate]);


  /* ------------------------------------------------------------------------
   * Derived values
   * ---------------------------------------------------------------------- */

  const camp =
    overview.data?.camp || {};

  const summary =
    overview.data?.summary || {};


  const campTitle =
    camp?.branch_name ||
    camp?.location ||
    `Camp #${campId}`;


  const overallProgress =
    clamp(
      summary?.progress_percentage
    );


  const clientName =
    camp?.client?.name ||
    overview.data?.client?.name ||
    null;


  /* ------------------------------------------------------------------------
   * Render
   * ---------------------------------------------------------------------- */

  return (
    <div className="min-h-screen bg-slate-50/60">

      <div className="mx-auto max-w-7xl space-y-5 px-4 py-6 sm:px-6 lg:px-8">

        {/* ================================================================
         * Back Button
         * ============================================================ */}

        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0b7d7a] transition-colors hover:text-[#0a6360] focus:outline-none focus:ring-2 focus:ring-[#11a8a4] focus:ring-offset-2 rounded-lg"
        >
          <IconArrowLeft className="h-4 w-4" />

          Back to Dashboard
        </button>


        {/* ================================================================
         * Page Header
         * ============================================================ */}

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">

          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">

            <div className="min-w-0">

              <div className="flex flex-wrap items-center gap-2">

                <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                  {campTitle}
                </h1>

                {!overview.loading &&
                  !overview.error &&
                  overview.data && (
                    <StatusBadge
                      status={
                        overview.data?.status ||
                        (camp?.is_completed
                          ? 'COMPLETED'
                          : 'SCHEDULED')
                      }
                      size="md"
                    />
                  )}

              </div>


              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">

                {clientName && (
                  <span className="inline-flex items-center gap-1.5">
                    <IconBuilding className="h-4 w-4" />
                    {clientName}
                  </span>
                )}


                {camp?.location && (
                  <span className="inline-flex items-center gap-1.5">
                    <IconMapPin className="h-4 w-4" />
                    {camp.location}
                  </span>
                )}


                {camp?.start_date && (
                  <span className="inline-flex items-center gap-1.5">
                    <IconCalendar className="h-4 w-4" />
                    {formatDisplayDate(
                      camp.start_date
                    )}
                  </span>
                )}

              </div>

            </div>


            {/* Header Progress */}
            {!overview.loading &&
              !overview.error &&
              overview.data && (

                <div className="flex items-center gap-3 rounded-2xl border border-[#d9f1ef] bg-[#f5fbfb] px-4 py-3">

                  <RadialProgress
                    percentage={overallProgress}
                    size={60}
                    stroke={6}
                  />

                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                      Camp Progress
                    </p>

                    <p className="mt-0.5 text-lg font-bold tabular-nums text-[#0b7d7a]">
                      {Math.round(
                        overallProgress
                      )}%
                    </p>
                  </div>

                </div>
              )}

          </div>

        </div>


        {/* ================================================================
         * Tabs
         * ============================================================ */}

        <div className="overflow-x-auto">

          <div
            className="flex min-w-max gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-sm"
            role="tablist"
          >

            {TABS.map((tab) => {

              const Icon =
                tab.icon;

              const active =
                activeTab === tab.key;

              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() =>
                    setActiveTab(tab.key)
                  }
                  className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#11a8a4] focus:ring-offset-1 ${
                    active
                      ? 'bg-[#e6f7f6] text-[#0b7d7a] shadow-sm'
                      : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                  }`}
                >
                  <Icon className="h-4 w-4" />

                  {tab.label}

                  {tab.key === 'cases' &&
                    cases.loaded &&
                    cases.data?.total_cases !==
                      undefined && (
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-[10px] ${
                          active
                            ? 'bg-white text-[#0b7d7a]'
                            : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        {num(
                          cases.data.total_cases
                        )}
                      </span>
                    )}

                </button>
              );
            })}

          </div>

        </div>


        {/* ================================================================
         * Content
         * ============================================================ */}

        <div role="tabpanel">

          {activeTab === 'overview' && (
            <OverviewTab
                loading={
                overview.loading
                }
                error={
                overview.error
                }
                campData={
                overview.data
                }
                servicesData={
                services.data
                }
                campId={campId}
                financialTarget={
                financialTarget.data
                }
                financialTargetLoading={
                financialTarget.loading
                }
                financialTargetError={
                financialTarget.error
                }
                onFinancialTargetUpdated={
                refreshFinancialTarget
                }
            />
            )}


          {activeTab === 'cases' && (
            <CasesTab
              loading={
                cases.loading
              }
              error={
                cases.error
              }
              casesData={
                cases.data
              }
            />
          )}


          {activeTab === 'technicians' && (
            <TechniciansTab
              loading={
                technicians.loading
              }
              error={
                technicians.error
              }
              techData={
                technicians.data
              }
            />
          )}

        </div>

      </div>

    </div>
  );
}